import { Button } from "@gigpro/ui/Button";
import type { InputProps } from "@gigpro/ui/Input";
import { Input } from "@gigpro/ui/Input";
import { cn } from "@gigpro/ui/tailwind/cn";
import React from "react";

interface QuantitySelectorProps extends React.HTMLAttributes<HTMLDivElement> {
  value: number;
  maxValue?: number;
  minValue?: number;
  onValueChange: (quantity: number) => void;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
  /** A typed count is reported once, when the field is left or Enter is pressed; a stepper click still reports at once. */
  commitOnBlur?: boolean;
  /** Filled − and + beside a boxed count by default; "group" is one outlined box, where the page's controls are quiet. */
  look?: "buttons" | "group";
  inputProps?: Omit<
    InputProps,
    "className" | "inputClassName" | "type" | "disabled" | "value" | "onChange" | "invalid"
  >;
}

const constrainValue = (value: number, minValue: number, maxValue: number) => {
  if (value > maxValue) return maxValue;
  if (value < minValue) return minValue;
  return value;
};

export function QuantitySelector({
  value,
  minValue = 1,
  maxValue = 25,
  disabled,
  onValueChange: onChange,
  className,
  inputClassName,
  commitOnBlur = false,
  look = "buttons",
  inputProps,
  ...props
}: Readonly<QuantitySelectorProps>) {
  const [typed, setTyped] = React.useState<string | null>(null);
  const commit = () => {
    if (typed === null) return;
    setTyped(null);
    const next = constrainValue(Number(typed), minValue, maxValue);
    if (next !== value) onChange(next);
  };
  React.useEffect(() => {
    const constrainedValue = constrainValue(value, minValue, maxValue);
    if (value !== constrainedValue) onChange(constrainValue(value, minValue, maxValue));
  }, [minValue, maxValue, value, onChange]);

  const group = look === "group";
  const step = group
    ? "aspect-square h-full p-0 text-large font-regular"
    : "aspect-square h-[90%] p-0 text-enormous font-regular";

  return (
    <div
      className={cn(
        "inline-flex items-center",
        group ? "h-7.5 rounded-sm border border-light-gray focus-within:border-brand" : "h-8 gap-1.5",
        className,
      )}
      {...props}
    >
      <Button
        variant={group ? "text" : "contained"}
        type="button"
        className={step}
        disabled={disabled || value <= minValue}
        onClick={() => {
          setTyped(null);
          onChange(value - 1);
        }}
        data-testid="quantity-selector-decrement"
      >
        -
      </Button>
      <Input
        className={cn("h-full w-auto", (disabled || group) && "border-transparent", inputClassName)}
        inputClassName="px-2 py-1.5 text-center"
        type="number"
        disabled={disabled}
        value={commitOnBlur && typed !== null ? typed : String(value)}
        onChange={(e) =>
          commitOnBlur ? setTyped(e.target.value) : onChange(constrainValue(Number(e.target.value), minValue, maxValue))
        }
        onBlur={commitOnBlur ? commit : undefined}
        onKeyDown={commitOnBlur ? (e) => e.key === "Enter" && commit() : undefined}
        invalid={value < minValue || value > maxValue}
        {...inputProps}
        data-testid="pro-quantity-input"
      />
      <Button
        variant={group ? "text" : "contained"}
        type="button"
        className={step}
        disabled={disabled || value >= maxValue}
        onClick={() => {
          setTyped(null);
          onChange(value + 1);
        }}
        data-testid="quantity-selector-increment"
      >
        +
      </Button>
    </div>
  );
}
